// Shapes returned by the service_hours_* functions (db/migrations/0600 to 0605)
// and the display helpers the Service hours page and the driver board share.
//
// No figure is computed here. Offline hours, the suggested figure and its
// working, paid hours and the rand difference all arrive from Postgres
// (CLAUDE.md rule 4). This file only decides how a number is written, and that
// a figure Postgres did not send is shown as missing, never as zero.

export type ClaimStatus =
  | 'awaiting_manager'
  | 'awaiting_musa'
  | 'validated'
  | 'amended'
  | 'confirmed'
  | 'rejected'
  | 'withdrawn'

export type Cartrack = {
  trips?: number
  summary?: string
  site?: string
  arrived?: string
  departed?: string
  stops_at_site?: number
  stationary_hours?: number
  travel_hours?: number
  moving_hours?: number
  longest_stop_hours?: number
  other_cars_at_site?: number
  matches_booking_address?: boolean
}

export type Claim = {
  id: string
  ref: string
  shift_date: string
  shift_window: 'am_pm' | 'pm_am'
  driver_id: string
  driver_name: string | null
  plate: string | null
  manager_name: string | null
  answer_raw: string | null
  claimed_hours: number | null
  parsed_ok: boolean
  parse_note: string | null
  submitted_at: string | null
  record_deadline: string | null
  recorded_in_time: boolean | null
  online_hours: number | null
  offline_hours_on_shift: number | null
  cartrack: Cartrack | null
  other: {
    driver_yes_answers_prior_30_days?: number
    booking?: { ref: string; provider: string | null; address: string | null; scheduled_at: string; instructed_to_this_driver: boolean; instructed_in_writing: boolean } | null
    uber_basis?: string | null
  } | null
  suggested_hours: number | null
  suggestion_working: string | null
  evidence_at: string | null
  status: ClaimStatus
  status_label: string
  validated_hours: number | null
  decision: string | null
  note: string | null
  decided_by: string | null
  decided_at: string | null
  referred_by: string | null
  referred_at: string | null
  referral_note: string | null
  task_ref: number | null
  due: string | null
  escalated: boolean
  can_decide: boolean
  can_referee: boolean
  /** Why Validate cannot be used, decided in Postgres (0900). Null when it can. */
  validate_blocked: string | null
  /** The viewer is this driver's Driver Servant, or the referee on a referred claim. */
  is_mine: boolean
  /** The shift already pays the full shift hours, so no figure changes his pay. */
  already_full_pay: boolean
  paid_hours_now: number | null
  paid_hours_before_reasons: number | null
  unexplained_offline_hours: number | null
}

export type DriverRow = {
  driver_id: string
  driver_name: string
  claims: number
  claimed_hours: number | null
  pending_hours: number
  suggested_pending_hours: number | null
  validated_hours: number
  rejected: number
  paid_hours_now: number | null
  paid_hours_with_decided: number | null
  hours_difference: number | null
  unexplained_offline_hours: number | null
  rand_difference: number | null
}

export type PageData = {
  period: { start: string; end: string; label: string }
  viewer: { sees_money: boolean; is_referee: boolean; is_admin: boolean }
  referee_name: string
  switches: { addback_live: boolean; nmw_floor_live: boolean }
  claims: Claim[]
  drivers: DriverRow[]
}

export type ReportData = {
  period: { start: string; end: string }
  from: string
  claims: Claim[]
  drivers: {
    driver_id: string
    driver_name: string
    claims: number
    shifts: string
    claimed_hours: number | null
    suggested_hours: number | null
    gross_now: number | null
    gross_if_suggested: number | null
    rand_difference_if_suggested: number | null
    rand_difference_decided: number | null
  }[]
  nmw_floor_effect: { driver_id: string; driver_name: string; rand_difference: number }[]
}

export type DriverPortalClaim = {
  ref: string
  shift_date: string
  shift_window: 'am_pm' | 'pm_am'
  claimed: string | null
  claimed_hours: number | null
  status: ClaimStatus
  status_label: string
  decided_hours: number | null
  reason: string | null
  decided_at: string | null
}

const MISSING = '—'

function num(v: number | string | null | undefined): number | null {
  if (v === null || v === undefined || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

/** Hours as "1.50 h". A figure Postgres did not send is missing, not 0. */
export function formatHours(v: number | string | null | undefined): string {
  const n = num(v)
  return n === null ? MISSING : `${n.toFixed(2)} h`
}

/** A signed difference, "+2.40 h", for the by driver table. */
export function formatHoursDelta(v: number | string | null | undefined): string {
  const n = num(v)
  if (n === null) return MISSING
  return `${n > 0 ? '+' : ''}${n.toFixed(2)} h`
}

const ZAR = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/** Rand, written the way the payslip writes it: R4 500.00. */
export function formatZar(v: number | string | null | undefined, signed = false): string {
  const n = num(v)
  if (n === null) return MISSING
  const body = 'R' + ZAR.format(Math.abs(n)).replace(/,/g, ' ')
  if (n < 0) return '-' + body
  return signed && n > 0 ? '+' + body : body
}

export function windowLabel(w: string | null | undefined): string {
  return w === 'pm_am' ? 'Night shift' : w === 'am_pm' ? 'Day shift' : MISSING
}

export function formatDay(iso: string | null | undefined): string {
  if (!iso) return MISSING
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00Z` : iso)
  if (Number.isNaN(d.getTime())) return MISSING
  return d.toLocaleDateString('en-ZA', { timeZone: 'Africa/Johannesburg', weekday: 'short', day: 'numeric', month: 'short' })
}

/** Times arrive in UTC and are always shown in Johannesburg time. */
export function formatWhen(iso: string | null | undefined): string {
  if (!iso) return MISSING
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return MISSING
  return d.toLocaleString('en-ZA', {
    timeZone: 'Africa/Johannesburg', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  })
}

export type ChipTone = 'green' | 'red' | 'amber' | 'neutral' | 'brand'

export function statusTone(s: ClaimStatus): ChipTone {
  switch (s) {
    case 'awaiting_manager':
    case 'awaiting_musa':
      return 'amber'
    case 'validated':
    case 'amended':
    case 'confirmed':
      return 'green'
    case 'rejected':
      return 'red'
    default:
      return 'neutral'
  }
}

/**
 * Split the claims the page received into its three lists. Pure grouping of
 * the status Postgres assigned; nothing is recomputed.
 */
/**
 * The claims a viewer works on first: his own drivers', and referred claims if
 * he is the referee. Pure filtering on is_mine, which Postgres decides.
 */
export function mineOnly(claims: Claim[], only: boolean): Claim[] {
  return only ? claims.filter((c) => c.is_mine) : claims
}

export function groupClaims(claims: Claim[]): { manager: Claim[]; referee: Claim[]; decided: Claim[] } {
  const manager: Claim[] = []
  const referee: Claim[] = []
  const decided: Claim[] = []
  for (const c of claims) {
    if (c.status === 'awaiting_manager') manager.push(c)
    else if (c.status === 'awaiting_musa') referee.push(c)
    else decided.push(c)
  }
  return { manager, referee, decided }
}
